/**
 * Utility functions for formatting analysis results
 * Used in AdminPriceAnalysisManagement component
 */

// Extract confidence level from confidence value
export const getConfidenceLevel = (confidence) => {
  if (!confidence) return { level: "unknown", color: "gray", icon: "❓" };

  const value =
    typeof confidence === "string" ? parseFloat(confidence) : confidence;

  if (value >= 80) {
    return {
      level: "Cao",
      color: "green",
      icon: "🟢",
      badge: "bg-green-50 text-green-700 border-green-200",
    };
  } else if (value >= 50) {
    return {
      level: "Trung bình",
      color: "yellow",
      icon: "🟡",
      badge: "bg-yellow-50 text-yellow-700 border-yellow-200",
    };
  } else {
    return {
      level: "Thấp",
      color: "red",
      icon: "🔴",
      badge: "bg-red-50 text-red-700 border-red-200",
    };
  }
};

// Extract market trend indicator
export const getMarketTrendInfo = (trend) => {
  if (!trend) return { label: "N/A", icon: "➡️", color: "gray" };

  const trendLower = trend.toLowerCase();

  if (trendLower.includes("tăng") || trendLower.includes("up")) {
    return {
      label: "Tăng",
      icon: "📈",
      color: "green",
      badge: "bg-green-50 text-green-700",
    };
  } else if (trendLower.includes("giảm") || trendLower.includes("down")) {
    return {
      label: "Giảm",
      icon: "📉",
      color: "red",
      badge: "bg-red-50 text-red-700",
    };
  } else {
    return {
      label: "Ổn định",
      icon: "➡️",
      color: "gray",
      badge: "bg-gray-50 text-gray-700",
    };
  }
};

// Format price string
export const formatPrice = (price) => {
  if (!price) return "N/A";

  const str = price.toString().trim();

  // Already formatted
  if (str.includes("tỷ") || str.includes("triệu")) return str;

  const num = parseFloat(str);
  if (isNaN(num)) return str;

  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)} tỷ VNĐ`;
  } else if (num >= 1) {
    return `${num.toLocaleString("vi-VN")} triệu VNĐ`;
  }

  return str;
};

// Extract price per m2
export const formatPricePerM2 = (price) => {
  if (!price) return "N/A";

  const str = price.toString().trim();

  // Already formatted
  if (str.includes("/m²") || str.includes("/m2")) return str;

  const num = parseFloat(str);
  if (isNaN(num)) return str;

  return `${num.toLocaleString("vi-VN")} VNĐ/m²`;
};

// Get error type and categorization
export const analyzeError = (error) => {
  if (!error) return null;

  const errorStr = typeof error === "string" ? error : JSON.stringify(error);
  const lowerError = errorStr.toLowerCase();

  let errorType = "UNKNOWN_ERROR";
  let severity = "warning"; // info | warning | error | critical

  if (
    lowerError.includes("crawl") ||
    lowerError.includes("fetch") ||
    lowerError.includes("timeout")
  ) {
    errorType = "CRAWL_ERROR";
    severity = "error";
  } else if (
    lowerError.includes("parse") ||
    lowerError.includes("json") ||
    lowerError.includes("format")
  ) {
    errorType = "PARSING_ERROR";
    severity = "warning";
  } else if (
    lowerError.includes("validation") ||
    lowerError.includes("invalid") ||
    lowerError.includes("required")
  ) {
    errorType = "VALIDATION_ERROR";
    severity = "warning";
  } else if (
    lowerError.includes("network") ||
    lowerError.includes("connection")
  ) {
    errorType = "NETWORK_ERROR";
    severity = "error";
  } else if (
    lowerError.includes("auth") ||
    lowerError.includes("permission") ||
    lowerError.includes("unauthorized")
  ) {
    errorType = "AUTH_ERROR";
    severity = "critical";
  } else if (lowerError.includes("timeout")) {
    errorType = "TIMEOUT_ERROR";
    severity = "error";
  } else if (lowerError.includes("rate limit")) {
    errorType = "RATE_LIMIT_ERROR";
    severity = "warning";
  }

  return {
    errorType,
    severity,
    message:
      typeof error === "string"
        ? error
        : error.message || JSON.stringify(error),
    isRetryable:
      errorType === "CRAWL_ERROR" ||
      errorType === "NETWORK_ERROR" ||
      errorType === "TIMEOUT_ERROR" ||
      errorType === "RATE_LIMIT_ERROR",
  };
};

// Get error color/badge based on severity
export const getErrorBadge = (severity) => {
  const badges = {
    info: "bg-blue-50 text-blue-700 border-blue-200",
    warning: "bg-yellow-50 text-yellow-700 border-yellow-200",
    error: "bg-red-50 text-red-700 border-red-200",
    critical: "bg-purple-50 text-purple-700 border-purple-200",
  };

  return badges[severity] || badges.warning;
};

// Get error icon
export const getErrorIcon = (errorType) => {
  const icons = {
    CRAWL_ERROR: "🕷️",
    PARSING_ERROR: "🔧",
    VALIDATION_ERROR: "⚠️",
    NETWORK_ERROR: "📡",
    AUTH_ERROR: "🔐",
    TIMEOUT_ERROR: "⏱️",
    RATE_LIMIT_ERROR: "🚦",
    UNKNOWN_ERROR: "❌",
  };

  return icons[errorType] || "❌";
};

// Get suggested action for error
export const getErrorSuggestedAction = (errorType) => {
  const actions = {
    CRAWL_ERROR: "Kiểm tra URL hoặc thử lại",
    PARSING_ERROR: "Liên hệ support nếu lỗi tiếp tục",
    VALIDATION_ERROR: "Kiểm tra lại dữ liệu nhập vào",
    NETWORK_ERROR: "Kiểm tra kết nối mạng và thử lại",
    AUTH_ERROR: "Kiểm tra quyền truy cập hoặc thử login lại",
    TIMEOUT_ERROR: "Thử lại sau vài phút",
    RATE_LIMIT_ERROR: "Đợi một chút rồi thử lại",
    UNKNOWN_ERROR: "Liên hệ support để được hỗ trợ",
  };

  return actions[errorType] || "Thử lại hoặc liên hệ support";
};

// Extract property information from user message
export const parsePropertyInfoFromMessage = (message) => {
  if (!message) return {};

  const info = {
    address: "",
    project: "",
    area: "",
    type: "",
    bedrooms: "",
    bathrooms: "",
    floor: "",
    direction: "",
    legalStatus: "",
  };

  const lines = message.split("\n");

  lines.forEach((line) => {
    const lowerLine = line.toLowerCase();

    if (lowerLine.includes("địa chỉ:")) {
      info.address =
        line.split("Địa chỉ:")[1]?.trim() ||
        line.split("địa chỉ:")[1]?.trim() ||
        "";
    }
    if (lowerLine.includes("dự án:")) {
      info.project =
        line.split("Dự án:")[1]?.trim() ||
        line.split("dự án:")[1]?.trim() ||
        "";
    }
    if (lowerLine.includes("diện tích:")) {
      info.area =
        line.split("Diện tích:")[1]?.trim() ||
        line.split("diện tích:")[1]?.trim() ||
        "";
    }
    if (lowerLine.includes("loại bđs:") || lowerLine.includes("loại:")) {
      info.type =
        line.split("Loại BĐS:")[1]?.trim() ||
        line.split("loại:")[1]?.trim() ||
        "";
    }
    if (lowerLine.includes("phòng ngủ:") || lowerLine.includes("phòng ngủ")) {
      info.bedrooms =
        line.split("Phòng ngủ:")[1]?.trim() ||
        line.split("phòng ngủ:")[1]?.trim() ||
        "";
    }
    if (lowerLine.includes("phòng tắm:")) {
      info.bathrooms =
        line.split("Phòng tắm:")[1]?.trim() ||
        line.split("phòng tắm:")[1]?.trim() ||
        "";
    }
    if (lowerLine.includes("tầng:")) {
      info.floor =
        line.split("Tầng:")[1]?.trim() || line.split("tầng:")[1]?.trim() || "";
    }
    if (lowerLine.includes("hướng:")) {
      info.direction =
        line.split("Hướng:")[1]?.trim() ||
        line.split("hướng:")[1]?.trim() ||
        "";
    }
    if (lowerLine.includes("pháp lý:")) {
      info.legalStatus =
        line.split("Pháp lý:")[1]?.trim() ||
        line.split("pháp lý:")[1]?.trim() ||
        "";
    }
  });

  // Filter out empty values
  return Object.fromEntries(Object.entries(info).filter(([_, v]) => v !== ""));
};

// Format structured analysis result
export const formatStructuredAnalysis = (analysisResult) => {
  if (!analysisResult || typeof analysisResult !== "object") {
    return {
      price: null,
      pricePerM2: null,
      marketTrend: null,
      confidence: null,
      summary: "",
      marketInsights: [],
      recommendations: [],
    };
  }

  return {
    price: formatPrice(analysisResult.estimatedPrice),
    pricePerM2: formatPricePerM2(analysisResult.pricePerM2),
    marketTrend: analysisResult.marketTrend || "N/A",
    confidence: analysisResult.confidence,
    confidenceInfo: getConfidenceLevel(analysisResult.confidence),
    marketTrendInfo: getMarketTrendInfo(analysisResult.marketTrend),
    summary: analysisResult.summary || "",
    marketInsights: Array.isArray(analysisResult.marketInsights)
      ? analysisResult.marketInsights
      : [],
    recommendations: Array.isArray(analysisResult.recommendations)
      ? analysisResult.recommendations
      : [],
  };
};

// Get property type icon
export const getPropertyTypeIcon = (type) => {
  if (!type) return "🏢";

  const lowerType = type.toLowerCase();

  if (lowerType.includes("căn hộ") || lowerType.includes("apartment"))
    return "🏢";
  if (lowerType.includes("nhà riêng") || lowerType.includes("house"))
    return "🏠";
  if (lowerType.includes("đất") || lowerType.includes("land")) return "🌱";
  if (lowerType.includes("dự án") || lowerType.includes("project")) return "🏗️";
  if (lowerType.includes("shophouse") || lowerType.includes("shop"))
    return "🏪";
  if (lowerType.includes("biệt thự") || lowerType.includes("villa"))
    return "🏡";
  if (lowerType.includes("officetel") || lowerType.includes("office"))
    return "💼";

  return "🏢";
};
